import React, { useState, useEffect } from 'react';
import './TutorialOverlay.css';

const TutorialOverlay = ({ steps, onComplete, isVisible }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const [foundElement, setFoundElement] = useState(false);

    // Effectuer le suivi et le positionnement de l'élément actuel
    useEffect(() => {
        if (!isVisible || !steps[currentStep]) return;

        const updatePosition = () => {
            const selector = steps[currentStep].selector;
            const element = document.querySelector(selector);
            
            if (element) {
                const rect = element.getBoundingClientRect();
                setPosition({
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height
                });
                setFoundElement(true);
                console.log(`Élément trouvé pour le sélecteur: ${selector}`, rect);
            } else {
                setFoundElement(false);
                console.warn(`Élément non trouvé pour le sélecteur: ${selector}`);
            }
        };

        // Tentatives multiples pour trouver l'élément (DOM peut ne pas être prêt immédiatement)
        const checkForElement = setInterval(() => {
            updatePosition();
            if (foundElement) clearInterval(checkForElement);
        }, 300);

        // Nettoyer l'intervalle après 5 secondes de toute façon pour éviter les fuites mémoire
        setTimeout(() => clearInterval(checkForElement), 5000);
        
        // Positionner l'infobulle lorsque la fenêtre est redimensionnée
        window.addEventListener('resize', updatePosition);
        
        return () => {
            clearInterval(checkForElement);
            window.removeEventListener('resize', updatePosition);
        };
    }, [currentStep, steps, isVisible, foundElement]);

    // Gérer le passage à l'étape suivante
    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
            setFoundElement(false); // Réinitialiser pour la nouvelle recherche
        } else {
            // Marquer le tutoriel comme terminé
            localStorage.setItem('tutorialCompleted', 'true');
            onComplete();
        }
    };

    // Gérer le clic pour passer l'étape
    const handleSkip = () => {
        localStorage.setItem('tutorialCompleted', 'true');
        onComplete();
    };

    if (!isVisible || !steps[currentStep]) return null;

    const currentTutorialStep = steps[currentStep];
    const tooltipPosition = calculateTooltipPosition(
        position, 
        currentTutorialStep.position || 'bottom'
    );

    return (
        <div className="tutorial-overlay">
            <div className="tutorial-backdrop"></div>
            
            {/* Élément en focus */}
            {foundElement && (
                <div 
                    className="tutorial-highlight"
                    style={{
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        width: `${position.width}px`,
                        height: `${position.height}px`
                    }}
                ></div>
            )}
            
            {/* Infobulle */}
            {foundElement && (
                <div 
                    className={`tutorial-tooltip ${currentTutorialStep.position || 'bottom'}`}
                    style={tooltipPosition}
                >
                    <div className="tutorial-tooltip-arrow"></div>
                    <div className="tutorial-tooltip-content">
                        <h3>{currentTutorialStep.title}</h3>
                        <p>{currentTutorialStep.content}</p>
                        
                        <div className="tutorial-tooltip-actions">
                            <button onClick={handleSkip} className="tutorial-skip-button">
                                Skip Tutorial
                            </button>
                            <button onClick={handleNext} className="tutorial-next-button">
                                {currentStep < steps.length - 1 ? 'Next' : 'Finish'}
                            </button>
                        </div>
                        
                        <div className="tutorial-progress">
                            {steps.map((_, index) => (
                                <span 
                                    key={index} 
                                    className={`tutorial-dot ${index === currentStep ? 'active' : ''}`}
                                ></span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Calculer la position de l'infobulle en fonction de l'élément ciblé
const calculateTooltipPosition = (targetPosition, preferredPosition) => {
    const tooltipWidth = 300;  // Largeur estimée de l'infobulle
    const tooltipHeight = 180; // Hauteur estimée de l'infobulle
    const gap = 15;           // Espace entre l'élément et l'infobulle
    
    let top, left;
    
    switch (preferredPosition) {
        case 'top':
            top = targetPosition.top - tooltipHeight - gap;
            left = targetPosition.left + (targetPosition.width / 2) - (tooltipWidth / 2);
            break;
        case 'right':
            top = targetPosition.top + (targetPosition.height / 2) - (tooltipHeight / 2);
            left = targetPosition.left + targetPosition.width + gap;
            break;
        case 'left':
            top = targetPosition.top + (targetPosition.height / 2) - (tooltipHeight / 2);
            left = targetPosition.left - tooltipWidth - gap;
            break;
        case 'bottom':
        default:
            top = targetPosition.top + targetPosition.height + gap;
            left = targetPosition.left + (targetPosition.width / 2) - (tooltipWidth / 2);
            break;
    }
    
    // Ajuster la position pour éviter les débordements
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (left < 20) left = 20;
    if (left + tooltipWidth > viewportWidth - 20) left = viewportWidth - tooltipWidth - 20;
    
    if (top < 20) top = 20;
    if (top + tooltipHeight > viewportHeight - 20) top = viewportHeight - tooltipHeight - 20;
    
    return { top: `${top}px`, left: `${left}px` };
};

export default TutorialOverlay; 